import { useMemo } from 'react';
import { tenantIdentity, neutralIdentity } from '../../utils/tenantIdentity.js';
import { hatchRegion, burinPath } from '../../utils/etching.js';

/**
 * The etched plate behind the auth screens.
 *
 * Decorative in the strict sense — `aria-hidden`, no focus, no meaning that is
 * not also written out in text next to it. Someone who cannot see it loses
 * nothing, because the tenant is named in the card as well; the picture only
 * makes the answer to "which one am I on" arrive faster than reading does.
 *
 * Drawn rather than downloaded: an SVG coloured through tokens inherits both
 * themes for free, costs no request, and cannot be blocked the way a remote
 * asset can.
 *
 * Cut, not printed. The composition is still Bauhaus — a disc, a bar, a ring —
 * but the masses are not filled: the mass is hatched with burin strokes at the
 * tenant's angle, the counter-form is hatched across it or laid down as
 * aquatint grain, rings are cut in several turns. Solid are only the bar and
 * the small square — one full stroke and one stamp against a field of lines.
 *
 * No `viewBox`. The root SVG is the viewport in CSS pixels, and each shape sits
 * in its own nested `<svg>` whose `x`/`y` are the shape's anchor in percent.
 * Inside that nested viewport the geometry is plain pixels, so a 280-px disc is
 * 280 px on every screen and only its anchor travels with the window. Colour
 * and opacity are not in the markup at all — `data-tone` / `data-weight` are
 * resolved by the stylesheet, which is where the two themes diverge.
 */

const RAD = Math.PI / 180;

/** Three turns of the burin for a ring: radius offset, wobble x/y, width. */
const RING_PASSES = [
  [0, 0, 0, 1.1],
  [-2.5, 0.9, -0.6, 0.6],
  [1.6, -0.7, 0.8, 0.45],
];

/** The region a shape covers, in the frame it is hatched in (a slab's own). */
function regionOf(s) {
  switch (s.kind) {
    case 'disc':
      return { kind: 'disc', cx: s.cx, cy: s.cy, r: s.r };
    case 'arc': {
      const a = (s.angle || 0) * RAD;
      return { kind: 'arc', cx: s.cx, cy: s.cy, r: s.r, u: [Math.cos(a), Math.sin(a)] };
    }
    case 'slab':
      return { kind: 'rect', x0: s.cx - s.len / 2, y0: s.cy - s.thick / 2, x1: s.cx + s.len / 2, y1: s.cy + s.thick / 2 };
    default:
      return null;
  }
}

/** The geometry as one filled outline — what aquatint grain is laid onto. */
function Outline({ s, turned }) {
  switch (s.kind) {
    case 'disc':
      return <circle cx={s.cx} cy={s.cy} r={s.r} />;
    case 'arc':
      // A half-disc bulging to the right at 0°; `angle` turns it in quarter
      // steps around its own centre.
      return <path d={`M ${s.cx} ${s.cy - s.r} A ${s.r} ${s.r} 0 0 1 ${s.cx} ${s.cy + s.r} Z`} transform={turned} />;
    case 'slab':
      return <rect x={s.cx - s.len / 2} y={s.cy - s.thick / 2} width={s.len} height={s.thick} transform={turned} />;
    default:
      return null;
  }
}

function Strokes({ strokes }) {
  return strokes.map((st, i) => <path key={i} d={burinPath(st)} />);
}

function Etched({ s, hatch, seed, grainId }) {
  const state = { 'data-tone': s.tone, 'data-weight': s.weight };
  const turned = s.angle ? `rotate(${s.angle} ${s.cx} ${s.cy})` : undefined;

  switch (s.technique) {
    case 'hatch': {
      const region = regionOf(s);
      // A slab is hatched in its own frame and turned afterwards, so the hatch
      // keeps the tenant's angle on screen whatever the slab's lean.
      const local = s.kind === 'slab' ? s.angle || 0 : 0;
      // The counter-form is cut by the hand's second set, across the first.
      const base = s.role === 'counter' ? hatch.angle + (hatch.cross || 90) : hatch.angle;
      const strokes = hatchRegion(region, { ...hatch, angle: base - local }, seed);
      if (hatch.cross && s.role !== 'counter') {
        strokes.push(
          ...hatchRegion(
            region,
            { ...hatch, angle: base + hatch.cross - local, spacing: hatch.spacing * 1.35, weight: hatch.weight * 0.8 },
            seed + 1
          )
        );
      }
      return (
        <g {...state} transform={s.kind === 'slab' ? turned : undefined}>
          <Strokes strokes={strokes} />
        </g>
      );
    }
    case 'grain':
      return (
        <g {...state} filter={`url(#${grainId})`}>
          <Outline s={s} turned={turned} />
        </g>
      );
    case 'passes':
      return (
        <g {...state} data-stroke="true">
          {RING_PASSES.map(([dr, dx, dy, w], i) => (
            <circle key={i} cx={s.cx + dx} cy={s.cy + dy} r={s.r + dr} strokeWidth={w} />
          ))}
        </g>
      );
    case 'solid':
      if (s.kind === 'square') return <rect {...state} x={s.x} y={s.y} width={s.size} height={s.size} />;
      return (
        <g {...state}>
          <Outline s={s} turned={turned} />
        </g>
      );
    default:
      break;
  }

  if (s.kind === 'rule') {
    // Spans the nested viewport, which is as wide as the window; the
    // stylesheet pulls it in to the plate mark where there is one.
    return <rect {...state} className="etch-auth-backdrop__rule" x="0" y="0" width="100%" height={s.thick} />;
  }
  if (s.kind === 'dots') {
    const dots = [];
    for (let row = 0; row < s.rows; row += 1) {
      for (let col = 0; col < s.cols; col += 1) {
        dots.push(<circle key={`${row}-${col}`} cx={s.x + col * s.gap} cy={s.y + row * s.gap} r={s.r} />);
      }
    }
    return <g {...state}>{dots}</g>;
  }
  return null;
}

function Plate({ identity }) {
  const { hatch, seed, shapes } = identity;
  const grainId = `etch-aquatint-${seed}`;

  return (
    <svg focusable="false" role="presentation">
      <defs>
        {/* Aquatint: the shape's own colour, let through a field of grain.
            Luminance of fractal noise becomes alpha, the transfer table keeps
            only the bright specks, and the composite masks the fill with it —
            so the tone still comes from the token, not from the filter. */}
        <filter id={grainId} x="-2%" y="-2%" width="104%" height="104%" colorInterpolationFilters="sRGB">
          <feTurbulence type="fractalNoise" baseFrequency="0.7" numOctaves="3" seed={seed % 1000} result="noise" />
          <feColorMatrix in="noise" type="luminanceToAlpha" result="lum" />
          <feComponentTransfer in="lum" result="grain">
            <feFuncA type="table" tableValues="0 0 0 0.7 1" />
          </feComponentTransfer>
          <feComposite in="SourceGraphic" in2="grain" operator="in" />
        </filter>
      </defs>
      {shapes.map((shape, i) => {
        const etched = <Etched s={shape} hatch={hatch} seed={(seed + i * 7919) >>> 0} grainId={grainId} />;
        return (
          <svg
            key={`${shape.kind}-${i}`}
            x={`${shape.anchor[0]}%`}
            y={`${shape.anchor[1]}%`}
            width="100%"
            height="100%"
            overflow="visible"
          >
            {/* The group is what the stylesheet scales on small screens, so
                shapes shrink towards their anchor rather than drifting. A rule
                spans the window by definition and must not shrink with them. */}
            {shape.kind === 'rule' ? etched : <g className="etch-auth-backdrop__cluster">{etched}</g>}
          </svg>
        );
      })}
    </svg>
  );
}

export function TenantBackdrop({ slug }) {
  // No slug, no guessing: the neutral plate is hand-set, not hashed.
  const identity = useMemo(() => (slug ? tenantIdentity(slug) : neutralIdentity()), [slug]);
  // A few hundred strokes; cut once per tenant, not once per render.
  const plate = useMemo(() => (identity ? <Plate identity={identity} /> : null), [identity]);
  if (!plate) return null;

  return (
    <div className="etch-auth-backdrop" data-neutral={slug ? undefined : 'true'} aria-hidden="true">
      {plate}
    </div>
  );
}

export default TenantBackdrop;
